import React from 'react';
import { Box, VStack, HStack, Text, Badge, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { FiUsers, FiAlertTriangle } from 'react-icons/fi';
import { useUserContext } from '../context/UserContext';
import { useGetPlanInfo } from '../services/planService';

const EmployeeUsageCard = ({ 
  showUpgradePrompt = true,
  size = "md",
  variant = "subtle"
}) => {
  const { employeeLimits, isEmployeeLimitReached } = useUserContext();
  const { planInfo: planData } = useGetPlanInfo({ enabled: true });
  
  // Use plan data if available, otherwise fall back to context data
  const current = planData?.current_employees || employeeLimits?.current || 0;
  const limit = planData?.employee_limit || employeeLimits?.limit || 0;
  const remaining = planData?.remaining_employees || employeeLimits?.remaining || 0;
  
  const percentage = limit > 0 ? (current / limit) * 100 : 0;

  // Determine color based on usage
  const getProgressColor = () => {
    if (percentage >= 90) return 'red';
    if (percentage >= 75) return 'orange';
    return 'green';
  };

  const getBadgeColor = () => {
    if (isEmployeeLimitReached) return 'red';
    if (percentage >= 90) return 'orange';
    return 'green';
  };

  // Show component even if plan data is not loaded yet
  return (
    <Box p={4} bg="white" borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
      <VStack spacing={4} align="stretch">
        {/* Employee Usage Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <FiUsers size={16} />
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              Employee Usage
            </Text>
          </HStack>
          <Badge colorScheme={getBadgeColor()} variant={variant}>
            {current} employees
          </Badge>
        </HStack>

        {/* Progress Bar */}
        <Box>
          <Box
            w="100%"
            h="8px"
            bg="gray.200"
            borderRadius="full"
            overflow="hidden"
          >
            <Box
              h="100%"
              bg={`${getProgressColor()}.500`}
              borderRadius="full"
              transition="width 0.3s ease"
              width={`${Math.min(percentage, 100)}%`}
            />
          </Box>
          <Text fontSize="xs" color="gray.500" mt={1}>
            {remaining} employees remaining
          </Text>
        </Box>

        {/* Employee Usage Details */}
        <Box p={3} bg="gray.50" borderRadius="md">
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.600">Employees Remaining:</Text>
              <Text fontSize="xs" fontWeight="600" color="gray.700">
                {remaining} employees
              </Text>
            </HStack>
          </VStack>
        </Box>

        {/* Employee Limit Warnings */}
        {isEmployeeLimitReached && showUpgradePrompt && (
          <Alert status="error" borderRadius="md">
            <AlertIcon as={FiAlertTriangle} />
            <Box>
              <AlertTitle>Employee Limit Reached</AlertTitle>
              <AlertDescription>
                You've reached your employee limit. Please upgrade your plan to add more employees.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {percentage >= 90 && !isEmployeeLimitReached && showUpgradePrompt && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Approaching Employee Limit</AlertTitle>
              <AlertDescription>
                You're approaching your employee limit. Consider upgrading your plan.
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};

export default EmployeeUsageCard; 